import * as THREE from 'three';
import { sampleHeight } from '../../shared/waveField';
import { islandFloor } from '../../shared/islandField';
import { InputController, helmTurnFromInput, resolveDeckLocalMove, sailRotateFromInput } from '../core/InputController';
import { LookController } from '../core/LookController';
import { Loop } from '../core/Loop';
import { PerfOverlay, type PerfSnapshot } from '../systems/PerfOverlay';
import { DEBUG_ENABLED, getDebugWeatherMode } from '../systems/DebugFlags';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { PlayerAvatar } from '../entities/PlayerAvatar';
import { AnchorRig } from '../entities/AnchorRig';
import { CannonballRenderer } from '../entities/Cannonball';
import { CombatFx } from '../entities/CombatFx';
import { DeckCannons } from '../entities/CannonModels';
import { CrewAvatars } from '../entities/CrewAvatars';
import { DamageFx } from '../entities/DamageFx';
import { ModelLibrary } from '../entities/ModelLibrary';
import { StructureModels, type StructureSnapshot } from '../entities/StructureModels';
import { BuildMode } from '../systems/BuildMode';
import { BuildTour } from '../systems/BuildTour';
import { Seagulls } from '../entities/Seagulls';
import { SeaLife } from '../entities/SeaLife';
import { Ship, STATION_ANCHORS, type ClothOption, type ShipPalette } from '../entities/Ship';
import { ShipWake } from '../entities/ShipWake';
import { WindParticles } from '../entities/WindParticles';
import { ColyseusClient, type ConnectionStatus, type NetState, type PlayerLike, type SailLike, type WeatherLike } from '../net/ColyseusClient';
import { AudioSystem } from '../systems/AudioSystem';
import { StormAudio } from '../systems/StormAudio';
import { CameraRig } from '../systems/CameraRig';
import { WorldStreamer } from '../systems/WorldStreamer';
import { DebugTools, type DebugTuning } from '../systems/DebugTools';
import { Hud, type HudCrew, type HudRoleKind } from '../systems/Hud';
import { InterpolationSystem } from '../systems/InterpolationSystem';
import { InputTelemetry, type InputBranch, type InputSnapshot, type LastControlMessage } from '../systems/InputTelemetry';
import { Minimap } from '../systems/Minimap';
import { OceanSystem } from '../systems/OceanSystem';
import { PostFxSystem } from '../systems/PostFxSystem';
import { SeaState } from '../systems/SeaState';
import { SeaViz, type SeaVizFrame } from '../systems/SeaViz';
import { SkySystem } from '../systems/SkySystem';
import { WaveClock } from '../systems/WaveClock';
import { WaterFoam } from '../systems/WaterFoam';
import { WeatherSystem } from '../systems/WeatherSystem';
import { createSeededRandom } from '../utils/random';

const SAIL_COUNT = 2;
const DECK_HALF_LENGTH = 8.0;
const DECK_HALF_BEAM = 2.8;
const DECK_HEIGHT = 1.9;
const FP_EYE_HEIGHT = 1.42;
const INTERACT_RANGE = 2.4;
const PLAYER_MOVE_SPEED = 3.2;
/** Prototype ashore mode: walk speed, leash radius and board range (metres). */
const ASHORE_SPEED = 3.4;
const ASHORE_MAX_RADIUS = 140;
const BOARD_DISTANCE = 14;
/** Boarding rope: port rail amidships, where `E` steps off the hull. */
const ROPE_POINT = { x: 0, y: -DECK_HALF_BEAM };
const ROPE_RADIUS = 1.8;
const SAIL_ROTATE_SPEED = 1.1;
const SAIL_STEP_INTERVAL = 0.28;
const INTERPOLATION_DELAY = 0.12;
/**
 * Buoyancy probe offsets. MUST match the authoritative `SHIP.probeForeAft` /
 * `SHIP.probeBeam` (`server/src/config.ts`) and the `+Z = port` convention in
 * `server/src/systems/BuoyancySystem.ts`. The client uses these only to publish
 * the verification numbers — never to move the ship.
 */
const PROBE_FORE_AFT = 7.5;
const PROBE_BEAM = 2.4;
/**
 * Horizontal displacement between patches above which the ship is treated as
 * server-relocated (a debug teleport / respawn) and the interpolation is cut
 * instead of sliding across the map. Normal sailing moves << 1 m per patch.
 */
const TELEPORT_SNAP_DISTANCE = 30;
/**
 * Delay before revealing `#look-resume`. Entering first person requests the
 * lock on the same tick, so waiting for the request to settle keeps the overlay
 * from flashing on a normal `V` toggle.
 */
const LOOK_RESUME_REVEAL_DELAY_MS = 250;
/** Authoritative hull health ceiling (mirrors `SHIP.maxHealth` on the server). */
const HULL_MAX_HEALTH = 150;
/** Weather snapshot used before the first server patch arrives. */
const CALM_WEATHER: WeatherLike = { phase: 'calm', storm: 0, rain: 0, cloud: 0, fog: 0, gust: 0, lightning: false, seaScale: 1 };
/** Deterministic storm snapshot forced into named capture states. */
const CAPTURE_STORM_WEATHER: WeatherLike = { phase: 'storm', storm: 1, rain: 1, cloud: 1, fog: 0.85, gust: 0.4, lightning: false, seaScale: 1.85 };

/** Hostile recolour of the shared procedural hull for the AI enemy ship. */
const ENEMY_PALETTE: ShipPalette = {
  hull: 0x5a2230,
  deck: 0x7a4436,
  trim: 0x2a1218,
  sail: 0xd8c2c2,
  mast: 0x3a2028,
  helm: 0xd94f4f,
  sailStation: 0xb03a3a,
};

type SessionState = 'active-play' | 'sailing' | 'action';

interface PoseOverride {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  heading: number;
  speed: number;
  pitch: number;
  roll: number;
  sails: SailLike[];
}

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly postFx: PostFxSystem;
  private readonly sky: SkySystem;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(50, 1, 0.1, 2400);
  private readonly input: InputController;
  private readonly audio = new AudioSystem();
  private readonly stormAudio = new StormAudio(this.audio);
  private readonly hud = new Hud();
  /** Mobile profile gates optional ship detail (and other FX cost). */
  private readonly mobile =
    typeof window !== 'undefined' &&
    (window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 760);
  /** Cloth sail quality: 'high' desktop, 'low' mobile; `?cloth=off|low|high` overrides. */
  private readonly clothQuality: ClothOption = this.resolveClothQuality();
  private readonly ship = new Ship(
    SAIL_COUNT,
    {},
    this.mobile ? 'low' : 'high',
    undefined,
    this.clothQuality,
  );
  private readonly enemyShip = new Ship(
    SAIL_COUNT,
    ENEMY_PALETTE,
    this.mobile ? 'low' : 'high',
    undefined,
    this.clothQuality,
  );
  private readonly crewAvatars = new CrewAvatars(this.ship.group);
  private readonly damageFx = new DamageFx(this.ship.group);
  /** Bow anchor + rode cable: one merged draw call, island-field visibility. */
  private readonly anchorRig = new AnchorRig();
  private readonly shipCannons: DeckCannons;
  private readonly enemyCannons: DeckCannons;
  private readonly cannonballs: CannonballRenderer;
  private readonly combatFx: CombatFx;
  private readonly wake: ShipWake;
  private readonly seagulls: Seagulls;
  private readonly seaLife: SeaLife;
  private readonly cameraRig: CameraRig;
  private readonly ocean: OceanSystem;
  private readonly waterFoam: WaterFoam;
  private readonly net = new ColyseusClient();
  private readonly waveClock = new WaveClock();
  private readonly interpolation = new InterpolationSystem();
  private readonly enemyInterpolation = new InterpolationSystem();
  private readonly debugTools: DebugTools;
  private readonly minimap: Minimap;
  private readonly wind: WindParticles;
  private readonly seaState = new SeaState();
  private readonly seaViz: SeaViz;
  private readonly modelLibrary = new ModelLibrary();
  private readonly structureModels = new StructureModels(this.scene, this.modelLibrary);
  private readonly buildMode = new BuildMode(this.scene, {
    onPlace: (intent) => this.net.send('place_structure', intent),
  });
  private readonly weather: WeatherSystem;
  private readonly worldStreamer: WorldStreamer;
  private readonly perfOverlay: PerfOverlay;
  private readonly inputTelemetry: InputTelemetry;
  private readonly loop: Loop;

  private readonly tuning: DebugTuning = {
    maxSpeed: 9,
    turnRate: 0.9,
    thrustScale: 0.12,
    stiffness: 14,
    damping: 4.2,
    enemySteerGain: 1.6,
    collisionDamage: 22,
    cameraLag: 0.18,
    waveAmplitude: 1,
    waveSpeed: 1,
    grade: 1,
    exposure: 1.05,
    maxDpr: 2,
    masterVolume: 0.7,
  };

  private readonly avatars = new Map<string, PlayerAvatar>();
  private readonly localDeck = new THREE.Vector2(0, 1.9);
  private readonly moveInput = new THREE.Vector2();
  private readonly look: LookController;
  private readonly fpRig = new THREE.Object3D();
  private readonly fpWorldPos = new THREE.Vector3();
  private readonly fpWorldQuat = new THREE.Quaternion();
  private readonly posePosition = new THREE.Vector3();
  private readonly poseQuaternion = new THREE.Quaternion();
  private readonly enemyPosePosition = new THREE.Vector3();
  private readonly enemyPoseQuaternion = new THREE.Quaternion();
  private readonly euler = new THREE.Euler();
  private readonly muzzlePos = new THREE.Vector3();
  private readonly muzzleDir = new THREE.Vector3();
  private readonly smokeWindVec = new THREE.Vector3();
  private readonly cameraTarget = { position: new THREE.Vector3(), heading: 0, speed: 0 };

  private latestSails: SailLike[] = [];
  private latestHealth = 100;
  private repairing = false;
  private latestHeading = 0;
  private latestPitch = 0;
  private latestRoll = 0;
  private latestSpeed = 0;
  private latestEnemySails: SailLike[] = [];
  private latestEnemyHealth = 100;
  private latestEnemyHeading = 0;
  private latestEnemyPitch = 0;
  private latestEnemyRoll = 0;
  private latestEnemySpeed = 0;
  private latestState: NetState | null = null;
  private latestGameState = 'active';
  private connectionStatus = 'connecting';
  private poseOverride: PoseOverride | null = null;
  private enemyCaptureOverride: PoseOverride | null = null;
  private sessionState: SessionState = 'active-play';
  /** The control branch the last `handleInput` pass took (read by telemetry). */
  private currentInputBranch: InputBranch = 'offline';

  /**
   * Ashore mode mirrors the authoritative `PlayerState.ashore`. The local
   * position is predicted for responsiveness and reconciled to the room's
   * authoritative world position when idle; remotes read it directly from state.
   */
  private ashore = false;
  private readonly ashorePos = new THREE.Vector3();
  private ashoreYaw = 0;
  /** Last walk direction put on the wire, so an idle walker sends nothing. */
  private lastWalk = '';
  /** Last player-control message sent to the server, recorded at the send site. */
  private lastControl: LastControlMessage | null = null;
  /** Capture-only weather override so named states are reproducible storms. */
  private weatherOverride: WeatherLike | null = null;
  /** Set when a patch shows a server-side ship relocation; snaps the camera next frame. */
  private pendingCameraSnap = false;

  private frame = 0;
  private elapsed = 0;
  private pausedForScreenshot = false;
  /** Pointer-lock recovery hint (`#look-resume`), hidden unless look needs a click. */
  private lookResume: HTMLElement | null = null;
  /** Pending show of the hint, so a granted lock never flashes it. */
  private lookResumeTimer: number | null = null;
  private reducedMotion = false;
  private waveTime = 0;
  /** Authoritative (physics) wave amplitude — the value last sent via `debug_sea`. */
  private physicsSeaScale = 1;
  /** Rendered wave amplitude (weather multiplier × tuning), tracked for diagnostics. */
  private visualSeaScale = 1;
  /** `null` follows `?debug` (on); true/false forces the sea visualization. */
  private seaVizOverride: boolean | null = null;
  private sailRaiseTimer = 0;
  private sailLowerTimer = 0;
  private rng = createSeededRandom(1337);

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = this.tuning.exposure;
    this.perfOverlay = new PerfOverlay(this.renderer);
    this.loop = new Loop(
      (delta) => this.update(delta),
      () => this.render(),
      this.perfOverlay,
      () => this.buildPerfSnapshot(),
    );

    const mobile = this.mobile;
    this.ocean = new OceanSystem(this.scene, this.oceanWaveTable, { mobile });
    this.scene.add(this.ship.group);
    this.ship.group.add(this.fpRig);
    this.scene.add(this.enemyShip.group);
    // The anchor rig is world-space (its far end is the world-fixed drop
    // point), so it joins the scene, not the ship group.
    this.scene.add(this.anchorRig.group);
    this.worldStreamer = new WorldStreamer(this.scene, this.modelLibrary, {
      mobile,
      maxIslands: mobile ? 1 : 4,
      props: !mobile,
    });

    // Deck cannons sit in ship-local space, so adding them to the ship group
    // keeps them riding the same buoyancy and turning feedback as the hull.
    this.shipCannons = new DeckCannons();
    this.enemyCannons = new DeckCannons({
      barrel: 0x2a1a1a,
      carriage: 0x3a2020,
      metal: 0x141414,
    });
    this.ship.group.add(this.shipCannons.group);
    this.enemyShip.group.add(this.enemyCannons.group);
    this.cannonballs = new CannonballRenderer(this.scene);
    this.combatFx = new CombatFx(this.scene);
    this.cannonballs.onRemoved = (position, nearWater) => {
      if (nearWater) this.combatFx.splash(position);
      else this.combatFx.hitSparks(position);
    };

    this.seagulls = new Seagulls(this.scene);
    this.seaLife = new SeaLife(this.scene);

    // Underwater sea life must read through the surface, so let the water
    // shader blend over it instead of rendering fully opaque.
    this.ocean.water.material.transparent = true;
    this.ocean.water.material.uniforms.alpha.value = 0.88;
    this.wind = new WindParticles(this.scene);
    this.wake = new ShipWake(this.scene, (x, z) => this.waveHeight(x, z));
    this.waterFoam = new WaterFoam(this.scene);
    this.seaViz = new SeaViz(this.scene);

    this.cameraRig = new CameraRig(this.camera, {
      distance: 22,
      height: 9,
      lookAhead: 6,
      lag: this.tuning.cameraLag,
      clearance: 1.3,
    });
    this.minimap = new Minimap();

    const stick = this.getElement('#touch-stick');
    const knob = this.getElement('#touch-knob');
    const actionButton = this.getElement('#action-button');
    this.input = new InputController(stick, knob, actionButton);
    this.inputTelemetry = new InputTelemetry();
    this.inputTelemetry.setMobile(mobile);
    this.look = new LookController(this.canvas, {
      onLockStateChange: () => this.syncLookResumeHint(),
    });
    // Touch profiles never use pointer lock, so never offer to resume it.
    this.look.setLockAvailable(!mobile);
    this.lookResume = document.getElementById('look-resume');
    this.lookResume?.addEventListener('click', () => this.look.requestLock());
    // A canvas click is the user gesture browsers require to re-acquire the
    // lock after Esc or a refused request.
    this.canvas.addEventListener('click', () => {
      if (this.cameraRig.mode === 'firstPerson') this.look.requestLock();
    });
    document.getElementById('view-button')?.addEventListener('click', () => this.toggleCameraMode());

    this.debugTools = new DebugTools(this.tuning, {
      onChange: () => {
        this.renderer.toneMappingExposure = this.tuning.exposure;
        this.cameraRig.tuning.lag = this.tuning.cameraLag;
        this.audio.setMasterVolume(this.tuning.masterVolume);
        resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
      },
      onServerTuning: (partial) => this.net.send('debug_tuning', partial),
      onSeaScale: (value) => this.applySeaScale(value),
    });

    this.net.onStatus = (status) => {
      this.connectionStatus = status;
      this.updateLoadingScreen(status);
    };
    this.net.onPatch = (state) => this.onNetPatch(state);
    this.hud.onRestart = () => {
      if (this.net.status === 'connected') this.net.send('restart');
    };
    new BuildTour({
      mobile,
      onJump: (direction) => {
        // The server computes the destination from its own helpers; the client
        // sends only the direction and lets `onNetPatch` cut the interpolation
        // when the authoritative pose jumps.
        this.net.send('debug_build_tour', { direction });
        this.buildMode.setTourActive(direction === 'to_island');
        if (direction === 'to_ship') this.buildMode.setEnabled(false);
      },
    });
    this.net.connect();

    this.audio.setMasterVolume(this.tuning.masterVolume);
    this.ship.setPose(this.posePosition, this.poseQuaternion);
    this.ship.updateSails([]);
    this.enemyShip.setPose(this.enemyPosePosition, this.enemyPoseQuaternion);
    this.enemyShip.updateSails([]);
    this.cameraRig.snapTo(this.cameraTarget);
    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);

    // Sky dome + PBR environment map, then the post-processing chain that
    // renders the scene (bloom, grade/vignette, SMAA on mobile).
    this.sky = new SkySystem(this.scene, this.renderer, mobile);
    this.scene.environment = this.sky.environment;
    this.postFx = new PostFxSystem(this.renderer, this.scene, this.camera, mobile);
    this.weather = new WeatherSystem(this.scene, this.camera, mobile);
    this.preloadModels();

    this.installOrbitControls();
    this.installBuildControls();
    this.installTestHooks();
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.perfOverlay.dispose();
    this.input.dispose();
    this.look.dispose();
    this.audio.dispose();
    this.stormAudio.dispose();
    this.debugTools.dispose();
    this.inputTelemetry.dispose();
    this.minimap.dispose();
    this.net.dispose();
    this.ocean.dispose();
    this.ship.dispose();
    this.seaViz.dispose();
    this.enemyShip.dispose();
    this.shipCannons.dispose();
    this.enemyCannons.dispose();
    this.cannonballs.dispose();
    this.combatFx.dispose();
    this.crewAvatars.dispose();
    this.damageFx.dispose();
    this.anchorRig.dispose();
    this.seagulls.dispose();
    this.seaLife.dispose();
    this.wind.dispose();
    this.wake.dispose();
    this.waterFoam.dispose();
    this.worldStreamer.dispose();
    for (const avatar of this.avatars.values()) avatar.dispose();
    this.avatars.clear();
    this.weather.dispose();
    this.modelLibrary.dispose();
    this.structureModels.dispose();
    this.buildMode.dispose();
    window.removeEventListener('keydown', this.onBuildKeyDown);
    if (this.lookResumeTimer !== null) {
      clearTimeout(this.lookResumeTimer);
      this.lookResumeTimer = null;
    }
    this.postFx.dispose();
    this.sky.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private get oceanWaveTable() {
    return this.net.waveTable;
  }

  /** Resolve the cloth quality from `?cloth=` (debug override) or the profile. */
  private resolveClothQuality(): ClothOption {
    if (typeof window === 'undefined') return 'high';
    const param = new URLSearchParams(window.location.search).get('cloth');
    if (param === 'off' || param === 'low' || param === 'high') return param;
    return this.mobile ? 'low' : 'high';
  }

  private update(delta: number): void {
    this.frame += 1;

    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }
    if (!this.reducedMotion) this.elapsed += delta;

    if (resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr)) {
      this.postFx.resize();
    }
    const serverTime = this.latestState?.serverTime ?? null;
    this.waveClock.update(delta, serverTime, this.reducedMotion);
    this.waveTime = this.waveClock.time * this.tuning.waveSpeed;

    this.perfOverlay.mark('ocean');
    this.ocean.setWaveTable(this.oceanWaveTable);
    this.ocean.update(this.waveTime, this.posePosition);
    this.perfOverlay.mark('sky');
    this.sky.update(delta);
    this.perfOverlay.mark('atmosphere');
    this.applyAtmosphere(delta);
    this.stormAudio.update(delta);
    this.perfOverlay.mark('input');
    this.handleInput(delta);
    this.perfOverlay.mark('visuals');
    this.updateVisuals(delta);
    this.perfOverlay.mark('streamer');
    this.worldStreamer.update(this.posePosition);
    this.perfOverlay.mark('wake');
    this.wake.setViewport(this.renderer.domElement.height, this.camera.fov);
    this.wake.update(
      delta,
      this.posePosition,
      this.latestHeading,
      this.latestSpeed,
      this.latestGameState !== 'defeated',
    );
    this.perfOverlay.mark('foam');
    this.waterFoam.update(delta, this.posePosition, this.latestSpeed, (x, z) => this.waveHeight(x, z));
    this.perfOverlay.mark('combatFx');
    this.combatFx.update(delta);
    this.perfOverlay.mark('audio');
    this.audio.update(delta, {
      windStrength: this.latestState?.windStrength ?? 0,
      shipSpeed: this.latestSpeed,
      waveAmplitude: this.tuning.waveAmplitude,
      defeated: this.latestGameState === 'defeated',
    });
    this.perfOverlay.mark('diagnostics');
    this.publishDiagnostics();
  }

  private handleInput(delta: number): void {
    const state = this.latestState;
    const connected = this.net.status === 'connected' && state !== null;
    const firePort = this.input.consumeFirePort();
    const fireStarboard = this.input.consumeFireStarboard();

    if (this.latestGameState === 'defeated') {
      // Freeze crew input while the defeat overlay is up; the server ignores
      // it anyway, but this avoids spamming messages at the wreck.
      this.currentInputBranch = 'defeated';
      this.input.readMove(this.moveInput);
      return;
    }

    if (connected) {
      if (firePort) {
        this.net.send('fire_cannon', { side: 'port' });
        this.triggerMuzzleFlash('port');
      }
      if (fireStarboard) {
        this.net.send('fire_cannon', { side: 'starboard' });
        this.triggerMuzzleFlash('starboard');
      }
    }

    if (this.input.consumeToggleCamera()) this.toggleCameraMode();

    if (this.input.consumeAnchorToggle() && connected) {
      // Any crew member may drop/raise the shared anchor from anywhere at any
      // time (combat included); the server applies the same no-cost, no-limit
      // rule. Toggle on the authoritative flag so local and remote agree.
      const anchored = state.ship.anchored;
      this.sendControl(anchored ? 'raise_anchor' : 'drop_anchor', {});
    }

    this.syncAshoreState();

    if (this.input.consumeInteract()) {
      if (!connected) {
        // no-op while offline
      } else {
        this.releaseOverride();
        const player = state.players.get(this.net.sessionId);
        const stationId = player?.station ?? '';
        if (this.ashore) {
          // Ashore: E at the hull asks the room to board again (it enforces range).
          if (this.ashoreDistanceToShip() <= BOARD_DISTANCE) this.net.send('board');
        } else if (stationId !== '') {
          // Snap localDeck to the current station anchor so nearestStation()
          // uses the correct position when the player tries to switch.
          const currentIndex = Number(stationId);
          const anchor = STATION_ANCHORS[currentIndex];
          if (anchor) {
            this.localDeck.set(anchor.x, anchor.z);
          }
          // Look for a different nearby station to switch to directly.
          const nearest = this.nearestStationExcluding(currentIndex);
          if (nearest >= 0) {
            // Claim the new station; the server releases the old one first.
            this.net.send('claim_station', { index: nearest });
          } else {
            this.net.send('leave_station');
          }
        } else if (this.nearRope()) {
          // Free on deck at the boarding rope: ask the room to step off.
          this.net.send('disembark');
        } else {
          const nearest = this.nearestStation();
          if (nearest >= 0) this.net.send('claim_station', { index: nearest });
        }
      }
    }

    if (!connected) {
      this.currentInputBranch = 'offline';
      this.input.readMove(this.moveInput);
      return;
    }

    const player = state.players.get(this.net.sessionId);
    const stationId = player?.station ?? '';
    const stationIndex = stationId === '' ? -1 : Number(stationId);
    const station = stationIndex >= 0 ? state.ship.stations[stationIndex] : null;

    if (station && station.type === 'helm') {
      this.currentInputBranch = 'helm';
      const rotate = this.input.readRotationModifier();
      const turn = this.input.readHorizontal();
      if (rotate) {
        // Shift + A/D rotates every sail on the server; the helm axis must not
        // steer while the rotation modifier is held.
        if (Math.abs(turn) > 0.05) {
          this.releaseOverride();
          this.sendControl('sail_rotate', { delta: sailRotateFromInput(turn) * SAIL_ROTATE_SPEED * delta });
        }
      } else {
        if (Math.abs(turn) > 0.01) this.releaseOverride();
        // `turn` on the wire is a yaw-frame rudder order: positive = port.
        // The input axis runs the other way (right = +1), so convert here.
        this.sendControl('helm_input', { turn: helmTurnFromInput(turn) });
      }
      // W/S trims every sail from the helm (the server is captain-aware).
      this.stepSailLevels(this.input.readVertical(), delta);
      return;
    }

    if (station && station.type === 'sail') {
      this.currentInputBranch = 'sail';
      const horizontal = this.input.readHorizontal();
      if (Math.abs(horizontal) > 0.05) {
        this.releaseOverride();
        // Same conversion as the helm: right input must swing the boom to
        // starboard, so negate the right-positive axis before sending the
        // yaw-frame delta (positive `sail.angle` = port).
        this.sendControl('sail_rotate', { delta: sailRotateFromInput(horizontal) * SAIL_ROTATE_SPEED * delta });
      }
      this.stepSailLevels(this.input.readVertical(), delta);
      return;
    }

    if (this.ashore) {
      this.currentInputBranch = 'ashore';
      this.input.readMove(this.moveInput);
      if (this.moveInput.lengthSq() > 0.0001) this.releaseOverride();
      const predicted = this.moveAshore(this.moveInput, delta);
      const me = state.players.get(this.net.sessionId);
      if (!predicted && me) {
        // Idle: converge on the room's authoritative position.
        this.ashorePos.set(me.worldX, Math.max(islandFloor(me.worldX, me.worldZ), 0), me.worldZ);
        this.ashoreYaw = me.yaw;
      }
      return;
    }

    // Free-roaming on deck.
    this.currentInputBranch = 'deck';
    this.input.readMove(this.moveInput);
    if (this.moveInput.lengthSq() > 0.0001) {
      this.releaseOverride();
      // Camera-relative movement: resolve the screen-space input against the
      // ship heading and the active camera yaw, then turn the local avatar to
      // face travel. The authoritative server still owns the resulting deck
      // position via `deck_move`.
      const move = resolveDeckLocalMove(
        this.moveInput,
        this.latestHeading,
        this.latestHeading + this.cameraRig.movementYaw,
      );
      this.localDeck.x += move.dx * PLAYER_MOVE_SPEED * delta;
      this.localDeck.y += move.dz * PLAYER_MOVE_SPEED * delta;
      this.localDeck.x = THREE.MathUtils.clamp(this.localDeck.x, -DECK_HALF_LENGTH, DECK_HALF_LENGTH);
      this.localDeck.y = THREE.MathUtils.clamp(this.localDeck.y, -DECK_HALF_BEAM, DECK_HALF_BEAM);
      this.sendControl('deck_move', { x: this.localDeck.x, z: this.localDeck.y });
      this.avatars.get(this.net.sessionId)?.setFacing(Math.atan2(-move.dz, move.dx));
    } else {
      // Idle: reconcile the locally predicted deck position with the
      // authoritative server value so a dropped/denied `deck_move` can never
      // leave this client and the rest of the crew disagreeing about where
      // this player stands.
      const authoritative = state.players.get(this.net.sessionId);
      if (authoritative && authoritative.station === '') {
        this.localDeck.set(authoritative.deckX, authoritative.deckZ);
      }
    }
  }

  /**
   * Send a player-control message and record it for telemetry. The recorded
   * payload is the exact object put on the wire — after `helmTurnFromInput` and
   * any delta scaling — so the overlay and diagnostics show what the server was
   * told, not merely what the keys implied.
   */
  private sendControl(type: string, payload: Record<string, number | string | boolean>): void {
    this.net.send(type, payload);
    this.lastControl = { type, payload: { ...payload }, at: this.elapsed };
  }

  private stepSailLevels(vertical: number, delta: number): void {
    if (vertical < -0.35) {
      this.releaseOverride();
      this.sailRaiseTimer -= delta;
      if (this.sailRaiseTimer <= 0) {
        this.sendControl('sail_raise', {});
        this.sailRaiseTimer = SAIL_STEP_INTERVAL;
      }
      this.sailLowerTimer = 0;
    } else if (vertical > 0.35) {
      this.releaseOverride();
      this.sailLowerTimer -= delta;
      if (this.sailLowerTimer <= 0) {
        this.sendControl('sail_lower', {});
        this.sailLowerTimer = SAIL_STEP_INTERVAL;
      }
      this.sailRaiseTimer = 0;
    } else {
      this.sailRaiseTimer = SAIL_STEP_INTERVAL;
      this.sailLowerTimer = SAIL_STEP_INTERVAL;
    }
  }

  private toggleCameraMode(): void {
    const next = this.cameraRig.mode === 'firstPerson' ? 'chase' : 'firstPerson';
    this.cameraRig.setMode(next);
    this.look.setMode(next);
    if (next === 'firstPerson') {
      if (!this.mobile) this.look.requestLock();
    } else {
      this.look.exitLock();
    }
    this.syncLookResumeHint();
  }

  /**
   * Toggle the click-to-resume hint: first person on a pointer-lock profile
   * (desktop) with the lock not held. Touch profiles never see it, and neither
   * do paused capture states, so screenshots stay clean.
   *
   * The show is delayed: freshly entering first person requests the lock on the
   * same tick, so hiding immediately and revealing only after the request has
   * settled keeps the overlay from flashing on every `V` press.
   */
  private syncLookResumeHint(): void {
    const hint = this.lookResume;
    if (!hint) return;
    const shouldShow =
      !this.mobile &&
      !this.pausedForScreenshot &&
      this.cameraRig.mode === 'firstPerson' &&
      this.look.lockState === 'unlocked';
    if (this.lookResumeTimer !== null) {
      clearTimeout(this.lookResumeTimer);
      this.lookResumeTimer = null;
    }
    if (!shouldShow) {
      hint.hidden = true;
      return;
    }
    this.lookResumeTimer = window.setTimeout(() => {
      this.lookResumeTimer = null;
      hint.hidden = this.mobile || this.pausedForScreenshot || this.look.lockState !== 'unlocked';
    }, LOOK_RESUME_REVEAL_DELAY_MS);
  }

  private releaseOverride(): void {
    this.poseOverride = null;
    this.enemyCaptureOverride = null;
  }

  /** Cannon muzzle flash + drifting powder smoke for a local broadside. */
  private triggerMuzzleFlash(side: 'port' | 'starboard'): void {
    const muzzle = side === 'port'
      ? this.shipCannons.muzzleAnchors.port
      : this.shipCannons.muzzleAnchors.starboard;
    muzzle.getWorldPosition(this.muzzlePos);
    this.muzzleDir.set(0, 0, side === 'port' ? -1 : 1).applyQuaternion(this.poseQuaternion);
    this.combatFx.muzzleFlash(this.muzzlePos, this.muzzleDir);
    this.combatFx.smoke(this.muzzlePos, this.smokeWind());
  }

  /** Wind velocity (world units/second) toward which powder smoke drifts. */
  private smokeWind(): THREE.Vector3 {
    const direction = this.latestState?.windDirection ?? 0;
    const speed = (this.latestState?.windStrength ?? 0) * 5;
    this.smokeWindVec.set(Math.cos(direction) * speed, 0, -Math.sin(direction) * speed);
    return this.smokeWindVec;
  }

  /**
   * Drive the storm-driven atmosphere stack from the authoritative weather:
   * rain/lightning/fog (WeatherSystem), ocean sea state, wake spray, cinematic
   * grade/bloom and thunder shake.
   */
  private applyAtmosphere(delta: number): void {
    const weather = this.resolveWeather();
    this.weather.update(delta, weather);

    this.seaState.setStorm(weather.storm);
    const seaConfig = this.seaState.config;
    this.visualSeaScale = seaConfig.amplitude * this.tuning.waveAmplitude;
    this.ocean.setSeaState({
      ...seaConfig,
      amplitude: this.visualSeaScale,
    });
    this.wake.setSpray(seaConfig.spray);
    this.waterFoam.setSeaState(seaConfig);

    this.postFx.setStorm(weather.storm);
    this.postFx.setGrade(this.tuning.grade);
    this.stormAudio.setStorm(weather.storm);

    if (weather.lightning) this.cameraRig.addShake(0.5);
  }

  /** Preload the optional CC0 prop set; every consumer keeps a procedural fallback. */
  private preloadModels(): void {
    void this.modelLibrary.load([
      'crate',
      'barrel',
      'cannon',
      'chest',
      'island-rock',
      'island-rock-b',
      'island-rock-c',
      'palm',
      'hull-piece',
      'boat-row-large',
      'boat-row-small',
      'buoy',
    ]);
  }

  /** Deck position of the NPC repair hand (fallback mirrors the server anchor). */
  private repairAnchor(): { x: number; z: number } {
    for (const member of this.latestState?.crew ?? []) {
      if (member.role === 'repair') return { x: member.x, z: member.z };
    }
    return { x: -3.5, z: 0 };
  }

  private updateVisuals(delta: number): void {
    // Feed the cloth LOD with the live camera position (both ships).
    this.ship.setLodObserver(this.camera.position);
    this.enemyShip.setLodObserver(this.camera.position);
    const override = this.poseOverride;
    if (override) {
      this.posePosition.copy(override.position);
      this.poseQuaternion.copy(override.quaternion);
      this.latestHeading = override.heading;
      this.latestPitch = override.pitch;
      this.latestRoll = override.roll;
      this.latestSpeed = override.speed;
      this.latestSails = override.sails;
    } else {
      const pose = this.interpolation.sample(this.waveClock.time - INTERPOLATION_DELAY);
      if (pose) {
        this.posePosition.copy(pose.position);
        this.poseQuaternion.copy(pose.quaternion);
        this.euler.setFromQuaternion(pose.quaternion, 'YXZ');
        this.latestHeading = this.euler.y;
        this.latestPitch = this.euler.x;
        this.latestRoll = this.euler.z;
      } else {
        this.posePosition.set(0, this.waveHeight(0, 0), 0);
        this.poseQuaternion.setFromEuler(new THREE.Euler(0, 0, 0, 'YXZ'));
        this.latestHeading = 0;
        this.latestPitch = 0;
        this.latestRoll = 0;
      }
      const state = this.latestState;
      if (state) {
        this.latestSpeed = state.ship.speed;
        this.latestSails = Array.from(state.ship.sails);
      }
    }

    this.ship.setPose(this.posePosition, this.poseQuaternion);
    // Anchor rig: stowed it hangs at the bow; anchored the rode runs bow →
    // drop point and the anchor shows only where the island field has a floor.
    this.anchorRig.update(
      this.latestState
        ? { anchored: this.latestState.ship.anchored, x: this.latestState.ship.anchorX, z: this.latestState.ship.anchorZ }
        : { anchored: false, x: this.posePosition.x, z: this.posePosition.z },
      this.posePosition,
      this.poseQuaternion,
    );
    this.ship.setTurnHeel(this.latestState?.ship.rudder ?? 0, this.latestSpeed, delta);
    this.ship.update(
      delta,
      this.latestState?.windDirection ?? 0,
      this.latestState?.windStrength ?? 0,
    );
    this.ship.updateSails(this.latestSails);

    // Update station marker colours based on occupancy
    const stations = this.latestState?.ship.stations;
    const occupied: boolean[] = [];
    if (stations) {
      for (const station of stations) {
        occupied.push(station.occupiedBy !== '');
      }
    }
    this.ship.updateStationMarkers(occupied, delta);
    this.seagulls.update(delta, this.elapsed, this.posePosition.x, this.posePosition.y, this.posePosition.z);
    this.seaLife.update(delta, this.elapsed, this.posePosition.x, this.posePosition.z);
    this.wind.update(
      delta,
      this.posePosition.x,
      this.posePosition.z,
      this.latestState?.windDirection ?? 0,
      this.latestState?.windStrength ?? 0,
    );

    this.updateAvatars();
    this.crewAvatars.update(delta);
    this.updateEnemyShip(delta);
    this.cannonballs.update(delta, this.latestState?.cannonballs ?? [], (x, z) => this.waveHeight(x, z));

    this.cameraTarget.position.copy(this.posePosition);
    this.cameraTarget.heading = this.latestHeading;
    this.cameraTarget.speed = this.latestSpeed;
    if (this.pendingCameraSnap) {
      this.pendingCameraSnap = false;
      this.cameraRig.snapTo(this.cameraTarget);
    }
    this.look.applyTo(this.cameraRig);
    const fpPlayer = this.latestState?.players.get(this.net.sessionId);
    if (fpPlayer) {
      const [ax, , az] = this.avatarLocalPosition(this.net.sessionId, fpPlayer);
      this.fpRig.position.set(ax, DECK_HEIGHT + FP_EYE_HEIGHT, az);
      this.fpRig.quaternion.setFromEuler(
        new THREE.Euler(this.cameraRig.fp.pitch, this.cameraRig.fp.yaw - Math.PI / 2, 0, 'YXZ'),
      );
      this.fpRig.getWorldPosition(this.fpWorldPos);
      this.fpRig.getWorldQuaternion(this.fpWorldQuat);
    }
    if (this.ashore) {
      // Self-contained world-space follow camera for the prototype ashore mode:
      // the look yaw orbits the walking avatar directly, so the avatar's facing
      // never feeds back into the camera.
      const yaw = this.cameraRig.orbitYaw;
      const dist = 6.5;
      const height = 3.2;
      this.camera.position.set(
        this.ashorePos.x - Math.cos(yaw) * dist,
        this.ashorePos.y + height,
        this.ashorePos.z + Math.sin(yaw) * dist,
      );
      this.camera.up.set(0, 1, 0);
      this.camera.lookAt(this.ashorePos.x, this.ashorePos.y + 1.0, this.ashorePos.z);
    } else {
      this.cameraRig.update(delta, this.cameraTarget, (x, z) => this.waveHeight(x, z), {
        position: this.fpWorldPos,
        quaternion: this.fpWorldQuat,
      });
    }
    this.buildMode.update(this.camera, this.posePosition.x, this.posePosition.z, []);

    this.repairing =
      this.latestGameState !== 'defeated' &&
      (this.latestState?.repairing ?? false);

    const repairAnchor = this.repairAnchor();
    this.damageFx.setHealthFraction(this.latestHealth / HULL_MAX_HEALTH);
    this.damageFx.triggerRepair(this.repairing, repairAnchor.x, repairAnchor.z);
    this.damageFx.update(delta);

    this.hud.update({
      health: this.latestHealth,
      playerCount: this.latestState?.players.size ?? 1,
      speed: this.latestSpeed,
      heading: this.latestHeading,
      connection: this.connectionStatus,
      windDirection: this.latestState?.windDirection ?? 0,
      windStrength: this.latestState?.windStrength ?? 0,
      sails: this.latestSails,
      role: this.currentRole(),
      roleKind: this.currentRoleKind(),
      status: this.statusLine(),
      defeated: this.latestGameState === 'defeated',
      cannons: this.latestState
        ? Array.from(this.latestState.cannonStatus).map((cannon) => ({
            side: cannon.side === 'port' ? 'port' : 'starboard',
            loaded: cannon.loaded,
            cooldown: cannon.cooldown,
          }))
        : [],
      crew: this.crewSummary(),
      weather: this.resolveWeather(),
      anchored: this.latestState?.ship.anchored ?? false,
      anchorDistance: this.latestState?.ship.anchored ? this.anchorRig.distance : 0,
    });

    this.minimap.update(
      this.posePosition.x,
      this.posePosition.z,
      this.latestHeading,
      this.latestState?.windDirection ?? 0,
      this.latestState?.enemyShip ? [this.enemyPosePosition] : [],
    );

    this.debugTools.setLive({
      speed: this.latestSpeed,
      rudder: this.latestState?.ship.rudder ?? 0,
      windDirection: this.latestState?.windDirection ?? 0,
      windStrength: this.latestState?.windStrength ?? 0,
      sail0: this.latestSails[0] ? `${this.latestSails[0].level}` : '-',
      sail1: this.latestSails[1] ? `${this.latestSails[1].level}` : '-',
      seaScale: this.physicsSeaScale,
    });
    const buoy = this.sampleBuoyancy();
    this.debugTools.setBuoyancy({
      probes: buoy.probes.map((height) => height.toFixed(2)).join(' / '),
      target: `${buoy.target.heave.toFixed(2)} / ${buoy.target.pitch.toFixed(3)} / ${buoy.target.roll.toFixed(3)}`,
      actual: `${(this.latestState?.ship.y ?? this.posePosition.y).toFixed(2)} / ${this.latestPitch.toFixed(3)} / ${this.latestRoll.toFixed(3)}`,
    });
    this.updateSeaViz();
  }

  private readonly onBuildKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'KeyB') {
      this.buildMode.setEnabled(!this.buildMode.enabled);
      return;
    }
    if (!this.buildMode.enabled) return;
    if (event.code === 'KeyT') this.buildMode.rotateYaw(Math.PI / 4);
    else if (event.code === 'KeyG') this.buildMode.confirm();
  };

  private installBuildControls(): void {
    window.addEventListener('keydown', this.onBuildKeyDown);
  }

  private updateAvatars(): void {
    const state = this.latestState;
    if (!state) return;

    const seen = new Set<string>();
    for (const [id, player] of state.players) {
      seen.add(id);
      let avatar = this.avatars.get(id);
      if (!avatar) {
        avatar = new PlayerAvatar(Math.floor(this.rng() * 4), id === this.net.sessionId);
        this.avatars.set(id, avatar);
        this.ship.group.add(avatar.group);
      }
      const isLocal = id === this.net.sessionId;
      if (player.ashore) {
        // Ashore: render in world space. The local player uses its predicted
        // position; remotes read the authoritative one.
        if (avatar.group.parent !== this.scene) this.scene.add(avatar.group);
        if (isLocal) {
          avatar.setLocalPosition(this.ashorePos.x, this.ashorePos.y, this.ashorePos.z);
          avatar.setFacing(this.ashoreYaw);
        } else {
          const y = Math.max(islandFloor(player.worldX, player.worldZ), 0);
          avatar.setLocalPosition(player.worldX, y, player.worldZ);
          avatar.setFacing(player.yaw);
        }
      } else {
        if (avatar.group.parent !== this.ship.group) this.ship.group.add(avatar.group);
        avatar.setLocalPosition(...this.avatarLocalPosition(id, player));
      }
      // First person hides the local body so it never clips the view; remote
      // avatars stay visible.
      avatar.group.visible = !(isLocal && this.cameraRig.mode === 'firstPerson');
    }
    for (const [id, avatar] of this.avatars) {
      if (!seen.has(id)) {
        this.ship.group.remove(avatar.group);
        avatar.dispose();
        this.avatars.delete(id);
      }
    }
  }

  private avatarLocalPosition(id: string, player: PlayerLike): [number, number, number] {
    if (player.station !== '') {
      const anchor = this.ship.stationAnchor(Number(player.station));
      return [anchor.x, DECK_HEIGHT, anchor.z];
    }
    if (id === this.net.sessionId) {
      return [this.localDeck.x, DECK_HEIGHT, this.localDeck.y];
    }
    return [player.deckX, DECK_HEIGHT, player.deckZ];
  }

  /** True when the free-roaming local player stands at the boarding rope. */
  private nearRope(): boolean {
    return Math.hypot(this.localDeck.x - ROPE_POINT.x, this.localDeck.y - ROPE_POINT.y) <= ROPE_RADIUS;
  }

  private ashoreDistanceToShip(): number {
    return Math.hypot(this.ashorePos.x - this.posePosition.x, this.ashorePos.z - this.posePosition.z);
  }

  /**
   * Mirror the room's authoritative `ashore` flag. On the transition we snap to
   * the authoritative spawn (disembarking) or reset deck + camera (boarding).
   */
  private syncAshoreState(): void {
    const me = this.latestState?.players.get(this.net.sessionId);
    const authAshore = me?.ashore ?? false;
    if (authAshore === this.ashore) return;
    if (authAshore && me) {
      this.ashorePos.set(me.worldX, Math.max(islandFloor(me.worldX, me.worldZ), 0), me.worldZ);
      this.ashoreYaw = me.yaw;
      this.cameraRig.setMode('chase');
    } else {
      this.localDeck.set(0, DECK_HALF_BEAM);
      this.pendingCameraSnap = true;
    }
    this.ashore = authAshore;
    this.lastWalk = '';
  }

  /**
   * Predict the next ashore step locally and put only the normalized direction
   * on the wire (the room integrates it). Returns whether the player is moving.
   */
  private moveAshore(moveInput: { x: number; y: number }, delta: number): boolean {
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    dir.y = 0;
    if (dir.lengthSq() < 1e-6) dir.set(1, 0, 0);
    dir.normalize();
    const rightX = -dir.z;
    const rightZ = dir.x;
    const forward = -moveInput.y;
    const side = moveInput.x;
    const moving = moveInput.x * moveInput.x + moveInput.y * moveInput.y > 0.0001;
    let mx = dir.x * forward + rightX * side;
    let mz = dir.z * forward + rightZ * side;
    const len = Math.hypot(mx, mz);
    if (moving && len > 1e-4) {
      mx /= len;
      mz /= len;
      let nx = this.ashorePos.x + mx * ASHORE_SPEED * delta;
      let nz = this.ashorePos.z + mz * ASHORE_SPEED * delta;
      const dx = nx - this.posePosition.x;
      const dz = nz - this.posePosition.z;
      const dist = Math.hypot(dx, dz);
      if (dist > ASHORE_MAX_RADIUS) {
        nx = this.posePosition.x + (dx / dist) * ASHORE_MAX_RADIUS;
        nz = this.posePosition.z + (dz / dist) * ASHORE_MAX_RADIUS;
      }
      this.ashorePos.set(nx, Math.max(islandFloor(nx, nz), 0), nz);
      this.ashoreYaw = Math.atan2(mx, mz);
    }
    const key = moving ? `${mx.toFixed(3)},${mz.toFixed(3)}` : '0,0';
    if (key !== this.lastWalk) {
      this.sendControl('walk', { mx: moving ? mx : 0, mz: moving ? mz : 0 });
      this.lastWalk = key;
    }
    return moving;
  }

  /** Render the single synchronized enemy vessel from interpolated state. */
  private updateEnemyShip(delta: number): void {
    const override = this.enemyCaptureOverride;
    if (override) {
      // Framed action capture: place the enemy close ahead of the player
      // instead of reading the (far away, moving) authoritative pose.
      this.enemyShip.group.visible = true;
      this.enemyPosePosition.copy(override.position);
      this.enemyPoseQuaternion.copy(override.quaternion);
      this.latestEnemyHeading = override.heading;
      this.latestEnemyPitch = override.pitch;
      this.latestEnemyRoll = override.roll;
      this.latestEnemySpeed = override.speed;
      this.latestEnemyHealth = 100;
      this.latestEnemySails = override.sails;
      this.enemyShip.setPose(this.enemyPosePosition, this.enemyPoseQuaternion);
      this.enemyShip.update(
        delta,
        this.latestState?.windDirection ?? 0,
        this.latestState?.windStrength ?? 0,
      );
      this.enemyShip.updateSails(this.latestEnemySails);
      return;
    }

    const state = this.latestState;
    const hasEnemy = state !== null && state.enemyShip !== undefined;
    this.enemyShip.group.visible = hasEnemy;
    if (!hasEnemy) return;

    const pose = this.enemyInterpolation.sample(this.waveClock.time - INTERPOLATION_DELAY);
    if (pose) {
      this.enemyPosePosition.copy(pose.position);
      this.enemyPoseQuaternion.copy(pose.quaternion);
      this.euler.setFromQuaternion(pose.quaternion, 'YXZ');
      this.latestEnemyHeading = this.euler.y;
      this.latestEnemyPitch = this.euler.x;
      this.latestEnemyRoll = this.euler.z;
    } else {
      this.enemyPosePosition.set(state.enemyShip.x, state.enemyShip.y, state.enemyShip.z);
      this.enemyPoseQuaternion.setFromEuler(
        new THREE.Euler(state.enemyShip.pitch, state.enemyShip.rotationY, state.enemyShip.roll, 'YXZ'),
      );
      this.latestEnemyHeading = state.enemyShip.rotationY;
      this.latestEnemyPitch = state.enemyShip.pitch;
      this.latestEnemyRoll = state.enemyShip.roll;
    }
    this.latestEnemySpeed = state.enemyShip.speed;
    this.latestEnemyHealth = state.enemyShip.health;
    this.latestEnemySails = Array.from(state.enemyShip.sails);

    this.enemyShip.setPose(this.enemyPosePosition, this.enemyPoseQuaternion);
    this.enemyShip.update(
      delta,
      this.latestState?.windDirection ?? 0,
      this.latestState?.windStrength ?? 0,
    );
    this.enemyShip.updateSails(this.latestEnemySails);
  }

  private onNetPatch(state: NetState): void {
    const wasDefeated = this.latestGameState === 'defeated';
    const damage = this.latestHealth - state.ship.health;
    this.latestState = state;
    this.latestHealth = state.ship.health;
    this.latestGameState = state.gameState;
    this.crewAvatars.sync(Array.from(state.crew));
    this.structureModels.sync(Array.from(state.structures.values()) as StructureSnapshot[]);

    // Strong hull hits (cannon/ram) kick the camera; thunder is cued in
    // applyAtmosphere when the server publishes a lightning strike tick.
    if (damage > 0.5) {
      this.cameraRig.addShake(Math.min(0.6, Math.max(0.15, damage / 25)));
    }
    if (wasDefeated && state.gameState !== 'defeated') {
      // A restart snapped the ship back to spawn; drop the wreck snapshots so
      // the client cuts to the reset pose instead of sliding across the map.
      this.interpolation.reset();
      this.enemyInterpolation.reset();
    }
    // A debug teleport (build tour) moves the hull far beyond what one patch's
    // travel can produce, so cut the interpolation and snap the camera instead
    // of sliding across the intervening world.
    if (
      Math.hypot(state.ship.x - this.posePosition.x, state.ship.z - this.posePosition.z) >
      TELEPORT_SNAP_DISTANCE
    ) {
      this.interpolation.reset();
      this.enemyInterpolation.reset();
      this.pendingCameraSnap = true;
    }
    this.interpolation.push({
      time: state.serverTime,
      x: state.ship.x,
      y: state.ship.y,
      z: state.ship.z,
      yaw: state.ship.rotationY,
      pitch: state.ship.pitch,
      roll: state.ship.roll,
    });
    this.latestEnemyHealth = state.enemyShip.health;
    this.enemyInterpolation.push({
      time: state.serverTime,
      x: state.enemyShip.x,
      y: state.enemyShip.y,
      z: state.enemyShip.z,
      yaw: state.enemyShip.rotationY,
      pitch: state.enemyShip.pitch,
      roll: state.enemyShip.roll,
    });
  }

  private nearestStation(): number {
    let best = -1;
    let bestDistance = INTERACT_RANGE;
    for (let i = 0; i < STATION_ANCHORS.length; i += 1) {
      const anchor = STATION_ANCHORS[i];
      const distance = Math.hypot(this.localDeck.x - anchor.x, this.localDeck.y - anchor.z);
      if (distance <= bestDistance) {
        best = i;
        bestDistance = distance;
      }
    }
    return best;
  }

  /**
   * Like nearestStation(), but skips `excludeIndex` so the player does not
   * re-claim the station they are leaving. Used for one-press station switching.
   */
  private nearestStationExcluding(excludeIndex: number): number {
    let best = -1;
    let bestDistance = INTERACT_RANGE;
    for (let i = 0; i < STATION_ANCHORS.length; i += 1) {
      if (i === excludeIndex) continue;
      const anchor = STATION_ANCHORS[i];
      const distance = Math.hypot(this.localDeck.x - anchor.x, this.localDeck.y - anchor.z);
      if (distance <= bestDistance) {
        best = i;
        bestDistance = distance;
      }
    }
    return best;
  }

  private localStation() {
    const state = this.latestState;
    if (!state) return null;
    const player = state.players.get(this.net.sessionId);
    if (!player || player.station === '') return null;
    return state.ship.stations[Number(player.station)] ?? null;
  }

  private currentRoleKind(): HudRoleKind {
    const station = this.localStation();
    if (!station) return 'deck';
    return station.type === 'helm' ? 'helm' : 'sail';
  }

  private currentRole(): string {
    const station = this.localStation();
    if (!station) return 'deckhand';
    return station.type === 'helm' ? 'captain (helm)' : `sail ${station.sailIndex + 1}`;
  }

  private crewSummary(): HudCrew {
    let gunners = 0;
    let sailors = 0;
    let repairs = 0;
    for (const member of this.latestState?.crew ?? []) {
      if (member.role === 'gunner') gunners += 1;
      else if (member.role === 'sailor') sailors += 1;
      else if (member.role === 'repair') repairs += 1;
    }
    return { gunners, sailors, repairs, repairing: this.repairing };
  }

  /**
   * Remove the loading overlay immediately so a screenshot taken right after
   * connection (or a capture hook) can never include it, regardless of any CSS
   * transition timing. Inline styles win over transitioned classes.
   */
  private hideLoadingScreen(): void {
    const screen = document.querySelector<HTMLElement>('#loading-screen');
    if (!screen) return;
    screen.classList.add('hidden');
    screen.style.display = 'none';
    screen.style.opacity = '0';
  }

  private updateLoadingScreen(status: ConnectionStatus): void {
    const screen = document.querySelector<HTMLElement>('#loading-screen');
    if (!screen) return;
    if (status === 'connected') {
      this.hideLoadingScreen();
      return;
    }
    if (status === 'error' || status === 'disconnected') {
      const label = screen.querySelector<HTMLElement>('#loading-status-text');
      if (label) label.textContent = 'Connection failed. Retrying...';
    }
  }

  private statusLine(): string {
    if (this.net.status !== 'connected') return `Server ${this.net.status}`;
    return 'Set sails and steer — E to use a station';
  }

  private waveHeight(x: number, z: number): number {
    return sampleHeight(x, z, this.waveTime, this.tuning.waveAmplitude, this.ocean.activeWaveTable);
  }

  /** The authoritative amplitude the server samples; local mirror before the first patch. */
  private currentSeaScale(): number {
    const scale = this.latestState?.weather.seaScale ?? 0;
    return scale > 0 ? scale : this.physicsSeaScale;
  }

  /** CPU reference sampled at the authoritative (physics) amplitude. */
  private physicsWaveHeight(x: number, z: number): number {
    return sampleHeight(x, z, this.waveTime, this.currentSeaScale(), this.ocean.activeWaveTable);
  }

  /**
   * One paired sea amplitude: the authoritative CPU amplitude and the base
   * rendered amplitude move together, clamped to the server's `[0, 4]` range.
   */
  private applySeaScale(value: number): void {
    const scale = Math.max(0, Math.min(4, value));
    this.physicsSeaScale = scale;
    this.tuning.waveAmplitude = scale;
    this.net.send('debug_sea', { waveAmplitude: scale });
  }

  /**
   * The decision subset: the four probe water heights and the heave/pitch/roll
   * the server's damped spring is chasing. Mirrors
   * `server/src/systems/BuoyancySystem.ts` exactly; read-only, it never moves
   * the ship. Exposed through `diagnostics.buoyancy` for the sweep and the HUD.
   */
  private sampleBuoyancy(): {
    probes: [number, number, number, number];
    positions: Array<{ x: number; y: number; z: number }>;
    target: { heave: number; pitch: number; roll: number };
  } {
    const forwardX = Math.cos(this.latestHeading);
    const forwardZ = -Math.sin(this.latestHeading);
    const portX = -forwardZ;
    const portZ = forwardX;
    const cx = this.posePosition.x;
    const cy = this.posePosition.y;
    const cz = this.posePosition.z;
    const positions = [
      { x: cx + forwardX * PROBE_FORE_AFT, y: cy, z: cz + forwardZ * PROBE_FORE_AFT }, // bow
      { x: cx - forwardX * PROBE_FORE_AFT, y: cy, z: cz - forwardZ * PROBE_FORE_AFT }, // stern
      { x: cx + portX * PROBE_BEAM, y: cy, z: cz + portZ * PROBE_BEAM }, // port
      { x: cx - portX * PROBE_BEAM, y: cy, z: cz - portZ * PROBE_BEAM }, // starboard
    ];
    const probes = positions.map((p) => this.physicsWaveHeight(p.x, p.z)) as [
      number,
      number,
      number,
      number,
    ];
    const [bow, stern, port, starboard] = probes;
    return {
      probes,
      positions,
      target: {
        heave: (bow + stern + port + starboard) / 4,
        pitch: Math.atan2(bow - stern, 2 * PROBE_FORE_AFT),
        roll: Math.atan2(port - starboard, 2 * PROBE_BEAM),
      },
    };
  }

  /** Assemble the visualization frame from the same numbers as the diagnostics. */
  private buildSeaVizFrame(): SeaVizFrame {
    const { probes, positions, target } = this.sampleBuoyancy();
    const ship = this.latestState?.ship;
    return {
      probes: positions.map((position, index) => ({
        x: position.x,
        y: position.y,
        z: position.z,
        waterY: probes[index],
      })),
      target,
      actual: {
        heave: ship?.y ?? this.posePosition.y,
        pitch: this.latestPitch,
        roll: this.latestRoll,
      },
      vectors: {
        wind: {
          dir: this.latestState?.windDirection ?? 0,
          strength: this.latestState?.windStrength ?? 0,
        },
        // Forward speed is the visible consequence of the thrust model; the
        // server does not publish thrust separately.
        thrust: this.latestSpeed,
        // Server rudder is +starboard; the frame wants +port.
        rudder: -(ship?.rudder ?? 0),
        velocity: { x: ship?.vx ?? 0, z: ship?.vz ?? 0 },
      },
    };
  }

  /**
   * Debug-only sea visualization: off by default, forced by `setSeaViz`, and
   * always hidden while a capture is taken or on the mobile profile.
   */
  private updateSeaViz(): void {
    const visible =
      this.seaVizOverride ?? (DEBUG_ENABLED && !this.pausedForScreenshot && !this.mobile);
    this.seaViz.setVisible(visible);
    if (visible) this.seaViz.update(this.buildSeaVizFrame());
  }

  /**
   * Effective weather for the atmosphere, HUD and diagnostics. Priority:
   * capture override → debug override (`?weather=`, default `calm` while
   * diagnostics are on) → the authoritative server weather.
   */
  private resolveWeather(): WeatherLike {
    if (this.weatherOverride) return this.weatherOverride;
    switch (getDebugWeatherMode()) {
      case 'calm':
        return CALM_WEATHER;
      case 'storm':
        return CAPTURE_STORM_WEATHER;
      default:
        return this.latestState?.weather ?? CALM_WEATHER;
    }
  }

  private buildPerfSnapshot(): PerfSnapshot {
    const weather = this.resolveWeather();
    const snapshot: PerfSnapshot = {
      islandCount: this.worldStreamer.islandCount,
      weather: { phase: weather.phase, storm: weather.storm },
      cannonballs: this.latestState?.cannonballs.length ?? 0,
      playerCount: this.latestState?.players.size ?? 0,
    };
    if (this.enemyShip.group.visible) {
      snapshot.enemyDistance = this.enemyPosePosition.distanceTo(this.posePosition);
    }
    return snapshot;
  }

  private render(): void {
    this.perfOverlay.mark('postFx');
    this.postFx.render();
    this.perfOverlay.mark('postFx');
  }

  // ── Orbit camera controls (mouse drag / touch / scroll) ──────────
  private installOrbitControls(): void {
    const el = this.canvas;
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    // Track pinch zoom
    const pointers = new Map<number, { x: number; y: number }>();
    let lastPinchDist = 0;

    const SENSITIVITY_X = 0.005;
    const SENSITIVITY_Y = 0.003;
    const SCROLL_FACTOR = 0.001;

    el.addEventListener('pointerdown', (e: PointerEvent) => {
      // Only orbit on primary button (left click / touch)
      if (e.button !== 0) return;
      if (this.cameraRig.mode !== 'chase') return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) {
        dragging = true;
        lastX = e.clientX;
        lastY = e.clientY;
        el.setPointerCapture(e.pointerId);
      } else if (pointers.size === 2) {
        // Start pinch
        const pts = [...pointers.values()];
        lastPinchDist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      }
    });

    el.addEventListener('pointermove', (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      if (this.cameraRig.mode !== 'chase') return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.size === 2) {
        // Pinch zoom
        const pts = [...pointers.values()];
        const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
        if (lastPinchDist > 0) {
          this.cameraRig.orbitZoom *= lastPinchDist / dist;
          this.cameraRig.pokeOrbit();
        }
        lastPinchDist = dist;
        return;
      }

      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      this.cameraRig.orbitYaw -= dx * SENSITIVITY_X;
      this.cameraRig.orbitPitch += dy * SENSITIVITY_Y;
      this.cameraRig.pokeOrbit();
    });

    const endPointer = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) lastPinchDist = 0;
      if (pointers.size === 0) dragging = false;
    };
    el.addEventListener('pointerup', endPointer);
    el.addEventListener('pointercancel', endPointer);

    el.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      if (this.cameraRig.mode !== 'chase') return;
      this.cameraRig.orbitZoom += e.deltaY * SCROLL_FACTOR;
      this.cameraRig.pokeOrbit();
    }, { passive: false });
  }

  private installTestHooks(): void {
    // Deterministic hooks consumed by the Playwright suite, bot playtests and
    // the canvas inspector. Keep them real: silent no-ops make screenshots flaky.
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: async (name: string) => {
        if (name !== 'active-play' && name !== 'sailing' && name !== 'action') {
          throw new Error(`Unknown test state: ${name}`);
        }
        this.hideLoadingScreen();
        this.sessionState = name;
        // Capture freeze must happen only after the crew GLB is loaded (or its
        // load settled as a failure) so the hero shot shows modelled sailors,
        // not the procedural capsule fallback. The inspector awaits this hook,
        // so the freeze via setPausedForScreenshot(true) happens afterwards.
        await this.crewAvatars.whenReady();
        // Named captures are reproducible storm hero shots, independent of how
        // far the authoritative weather cycle has advanced.
        this.weatherOverride = CAPTURE_STORM_WEATHER;
        this.interpolation.reset();
        this.enemyInterpolation.reset();
        this.waveClock.reset();
        this.waveClock.update(0, name === 'sailing' ? 24 : 12);
        this.waveTime = this.waveClock.time * this.tuning.waveSpeed;
        this.ocean.setWaveTable(this.oceanWaveTable);
        this.poseOverride = name === 'sailing'
          ? this.makeSailingOverride()
          : name === 'action'
            ? this.makeActionOverride()
            : this.makeIdleOverride();
        this.enemyCaptureOverride = name === 'action' ? this.makeEnemyActionOverride() : null;
        // Rest both hulls on the live wave surface so the hero shot does not
        // show the vessels floating above (or sunk below) the water line.
        this.poseOverride.position.y = this.waveHeight(
          this.poseOverride.position.x,
          this.poseOverride.position.z,
        );
        if (this.enemyCaptureOverride) {
          this.enemyCaptureOverride.position.y = this.waveHeight(
            this.enemyCaptureOverride.position.x,
            this.enemyCaptureOverride.position.z,
          );
        }
        this.frameShipForCapture();
        this.updateVisuals(0);
        this.cameraRig.snapTo(this.cameraTarget);
        this.applyAtmosphere(0);
        this.ocean.update(this.waveTime, this.posePosition);
        this.warmUpFx();
        if (name === 'action') this.fireCaptureBroadside();
        this.render();
        const shipNdc = this.posePosition.clone().project(this.camera);
        const shipScreen = {
          x: (shipNdc.x * 0.5 + 0.5) * this.canvas.clientWidth,
          y: (1 - (shipNdc.y * 0.5 + 0.5)) * this.canvas.clientHeight,
        };
        this.publishDiagnostics();
        return { state: name, shipScreen };
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
        this.inputTelemetry.setPausedForScreenshot(paused);
        this.syncLookResumeHint();
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
        this.render();
        this.publishDiagnostics();
      },
      hideDebugUi: (hidden: boolean) => {
        this.debugTools.setHidden(hidden);
      },
      setWaveAmplitude: (value: number) => {
        this.physicsSeaScale = Math.max(0, Math.min(4, value));
        this.net.send('debug_sea', { waveAmplitude: this.physicsSeaScale });
      },
      setSeaScale: (value: number) => {
        this.applySeaScale(value);
        this.publishDiagnostics();
      },
      setSeaViz: (visible: boolean | null) => {
        this.seaVizOverride = visible;
      },
      setOrbit: (yaw: number) => {
        this.cameraRig.orbitYaw = yaw;
        this.cameraRig.pokeOrbit();
      },
      setCameraMode: (mode: 'chase' | 'firstPerson') => {
        this.cameraRig.setMode(mode);
        this.look.setMode(mode);
        this.syncLookResumeHint();
        this.render();
        this.publishDiagnostics();
      },
      setPointerLockState: (state: 'locked' | 'unlocked' | 'unavailable' | null) => {
        // Deterministic pointer-lock state without a real lock: headless
        // Chromium cannot hold one (microsoft/playwright#20956).
        if (state === null) {
          this.look.setLockedOverride(null);
          this.look.setLockAvailable(!this.mobile);
        } else {
          this.look.setLockAvailable(state !== 'unavailable');
          this.look.setLockedOverride(state === 'unavailable' ? null : state === 'locked');
        }
        this.syncLookResumeHint();
        this.publishDiagnostics();
      },
      setInputOverlay: (visible: boolean | null) => {
        this.inputTelemetry.setForcedVisible(visible);
        this.publishDiagnostics();
      },
      placeStructure: (type: string, x: number, z: number, yaw: number) => {
        // Test-only: send the same authoritative request the build ghost's
        // confirm() produces, so the build tour can be proven end to end
        // without driving the camera ray in a headless browser.
        this.net.send('place_structure', { type, x, z, yaw });
      },
    };
  }

  private makeIdleOverride(): PoseOverride {
    return {
      position: new THREE.Vector3(0, 0, 0),
      quaternion: new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, 0, 'YXZ')),
      heading: 0,
      speed: 0,
      pitch: 0,
      roll: 0,
      sails: [
        { level: 0, angle: 0 },
        { level: 0, angle: 0 },
      ],
    };
  }

  private makeSailingOverride(): PoseOverride {
    return {
      position: new THREE.Vector3(0, 1.6, 0),
      quaternion: new THREE.Quaternion().setFromEuler(new THREE.Euler(0.06, 0.7, -0.08, 'YXZ')),
      heading: 0.7,
      speed: 6.5,
      pitch: 0.06,
      roll: -0.08,
      sails: [
        { level: 4, angle: 0.18 },
        { level: 2, angle: -0.3 },
      ],
    };
  }

  /** Player pose for the action capture: bow +X, sails set, moving. */
  private makeActionOverride(): PoseOverride {
    return {
      position: new THREE.Vector3(0, 0, 0),
      quaternion: new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, 0, 'YXZ')),
      heading: 0,
      speed: 5,
      pitch: 0,
      roll: 0,
      sails: [
        { level: 3, angle: 0.15 },
        { level: 3, angle: -0.2 },
      ],
    };
  }

  /** Enemy framed close ahead of the player for the action capture. */
  private makeEnemyActionOverride(): PoseOverride {
    const position = new THREE.Vector3(36, 0, -8);
    // Face the player ship at the origin (bow +X, heading = atan2(-dz, dx)).
    const heading = Math.atan2(8, -36);
    return {
      position,
      quaternion: new THREE.Quaternion().setFromEuler(new THREE.Euler(0, heading, 0, 'YXZ')),
      heading,
      speed: 4,
      pitch: 0,
      roll: 0,
      sails: [
        { level: 4, angle: 0.15 },
        { level: 3, angle: -0.15 },
      ],
    };
  }

  /** Populate the pooled foam/spray so a frozen hero frame reads as a storm sea. */
  private warmUpFx(): void {
    for (let i = 0; i < 45; i += 1) {
      this.waterFoam.update(1 / 30, this.posePosition, this.latestSpeed, (x, z) => this.waveHeight(x, z));
    }
  }

  /**
   * Fire a frozen broadside so the action capture reads as live combat: muzzle
   * flashes and drifting powder smoke at both batteries, plus a hull hit on the
   * framed enemy. Flushed to the GPU immediately so the freeze shows full-bright
   * flashes rather than an empty particle pool.
   */
  private fireCaptureBroadside(): void {
    this.triggerMuzzleFlash('port');
    this.triggerMuzzleFlash('starboard');
    if (this.enemyCaptureOverride) {
      this.combatFx.hitSparks(this.enemyPosePosition);
    }
    this.combatFx.update(0);
  }

  /**
   * Swing the chase camera into a three-quarter hero view and pull back far
   * enough that hull, masts and rigging fill the frame on both the desktop and
   * the narrow mobile aspect ratio. Capture/test only: these are orbit offsets,
   * which the live camera auto-returns from during normal play.
   */
  private frameShipForCapture(): void {
    const mobile = this.mobile;
    if (mobile) {
      // The narrow mobile FOV already fills well with a low three-quarter view.
      this.cameraRig.orbitYaw = 0.72;
      this.cameraRig.orbitPitch = -0.2;
      this.cameraRig.orbitZoom = 1.7;
      return;
    }
    // Desktop: aim at the hull (lookAhead 0) so the vessel centres instead of
    // sliding to the bottom of the wide frame, and pull the chase seat in far
    // enough that hull + rigging fill a good part of it.
    this.cameraRig.tuning.lookAhead = 0;
    this.cameraRig.tuning.height = 6;
    this.cameraRig.tuning.distance = 26;
    this.cameraRig.orbitYaw = 1.0;
    this.cameraRig.orbitPitch = -0.1;
    this.cameraRig.orbitZoom = 1.0;
  }

  /**
   * Project the live input path into a serialisable snapshot. This is the single
   * source for both `diagnostics.input` and the overlay, so the two cannot
   * disagree. Read-only: `readHorizontal`/`readVertical`/`readPressedKeys` do not
   * consume or mutate input state.
   */
  private buildInputSnapshot(): InputSnapshot {
    const state = this.latestState;
    const player = state?.players.get(this.net.sessionId);
    const stationId = player?.station ?? '';
    return {
      keys: this.input.readPressedKeys(),
      horizontal: this.input.readHorizontal(),
      vertical: this.input.readVertical(),
      branch: this.resolveInputBranch(),
      station: stationId,
      role: this.currentRole(),
      cameraMode: this.cameraRig.mode,
      pointerLocked: this.look.locked,
      lastControl: this.lastControl,
    };
  }

  /**
   * The control branch consuming the axes. Build mode is a separate key path
   * (B/T/G) that overlays the movement branch, so it wins when enabled.
   */
  private resolveInputBranch(): InputBranch {
    if (this.latestGameState === 'defeated') return 'defeated';
    if (this.buildMode.enabled) return 'build';
    return this.currentInputBranch;
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    const input = this.buildInputSnapshot();
    const buoyancy = this.sampleBuoyancy();
    this.inputTelemetry.render(input, performance.now());
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      state: this.sessionState,
      gameState: this.latestGameState,
      connection: this.connectionStatus,
      roomId: this.net.roomId,
      sessionId: this.net.sessionId,
      // One entry per connected player. `deckX/deckZ` are the authoritative
      // server values; `x/z` are the rendered avatar positions in ship-local
      // deck space, so tests can assert that remote movement is visible.
      players: [...(this.latestState?.players.values() ?? [])].map((player) => {
        const avatar = this.avatars.get(player.id);
        return {
          id: player.id,
          isLocal: player.id === this.net.sessionId,
          deckX: player.deckX,
          deckZ: player.deckZ,
          station: player.station,
          ashore: player.ashore,
          worldX: player.worldX,
          worldZ: player.worldZ,
          x: avatar?.group.position.x ?? player.deckX,
          z: avatar?.group.position.z ?? player.deckZ,
        };
      }),
      playerCount: this.latestState?.players.size ?? 0,
      serverTime: this.latestState?.serverTime ?? 0,
      // Authoritative wind, sails and station occupancy are surfaced so the
      // Playwright suite can assert that input changes the authoritative sim
      // (not just the local prediction).
      wind: {
        direction: this.latestState?.windDirection ?? 0,
        strength: this.latestState?.windStrength ?? 0,
      },
      weather: this.resolveWeather(),
      repairing: this.repairing,
      islandCount: this.worldStreamer.islandCount,
      structures: this.latestState?.structures.size ?? 0,
      camera: {
        mode: this.cameraRig.mode,
        fpYaw: this.cameraRig.fp.yaw,
        fpPitch: this.cameraRig.fp.pitch,
        pointerLocked: this.look.locked,
        lookLockState: this.look.lockState,
        lockAttempts: this.look.lockRequestCount,
      },
      sails: (this.latestState?.ship.sails
        ? Array.from(this.latestState.ship.sails)
        : this.latestSails
      ).map((sail) => ({ level: sail.level, angle: sail.angle })),
      stations: Array.from(this.latestState?.ship.stations ?? []).map((station) => ({
        type: station.type,
        occupiedBy: station.occupiedBy,
        sailIndex: station.sailIndex,
      })),
      cannonballs: Array.from(this.latestState?.cannonballs ?? []).map((ball) => ({
        id: ball.id,
        x: ball.x,
        y: ball.y,
        z: ball.z,
      })),
      cannons: Array.from(this.latestState?.cannonStatus ?? []).map((cannon) => ({
        side: cannon.side,
        loaded: cannon.loaded,
        cooldown: cannon.cooldown,
      })),
      crew: Array.from(this.latestState?.crew ?? []).map((member) => ({
        id: member.id,
        role: member.role,
        x: member.x,
        z: member.z,
        station: member.station,
        task: member.task,
      })),
      // Anchor: the authoritative flag + drop point, and the client's
      // island-field visibility decision for it (Playwright asserts both).
      anchor: {
        anchored: this.latestState?.ship.anchored ?? false,
        x: this.latestState?.ship.anchorX ?? 0,
        z: this.latestState?.ship.anchorZ ?? 0,
        visible: this.anchorRig.visible,
        distance: this.anchorRig.distance,
      },
      // True once the crew GLB is loaded and every crew member has been
      // swapped from the procedural capsule fallback to the modelled sailor.
      crewModelsReady: this.crewAvatars.isModelReady(),
      models: {
        loaded: this.modelLibrary.report().loaded.length,
        failed: this.modelLibrary.report().failed.length,
      },
      input,
      deck: { x: this.localDeck.x, z: this.localDeck.y },
      ship: {
        position: {
          x: this.posePosition.x,
          y: this.posePosition.y,
          z: this.posePosition.z,
        },
        speed: this.latestSpeed,
        heading: this.latestHeading,
        pitch: this.latestPitch,
        roll: this.latestRoll,
        health: this.latestHealth,
      },
      enemyShip: {
        position: {
          x: this.enemyPosePosition.x,
          y: this.enemyPosePosition.y,
          z: this.enemyPosePosition.z,
        },
        speed: this.latestEnemySpeed,
        heading: this.latestEnemyHeading,
        pitch: this.latestEnemyPitch,
        roll: this.latestEnemyRoll,
        health: this.latestEnemyHealth,
        visible: this.enemyShip.group.visible,
      },
      // The decision subset for sea/buoyancy verification: the four probe water
      // heights and the target DOF (physics reference) vs the authoritative
      // actual pose. `seaScale` is the physics amplitude; `visualScale` is the
      // rendered one — if they differ, the seen sea and the floated sea diverge.
      buoyancy: {
        probes: buoyancy.probes,
        target: buoyancy.target,
        actual: {
          heave: this.latestState?.ship.y ?? this.posePosition.y,
          pitch: this.latestPitch,
          roll: this.latestRoll,
        },
        waveHeight: this.physicsWaveHeight(this.posePosition.x, this.posePosition.z),
        seaScale: this.currentSeaScale(),
        visualScale: this.visualSeaScale,
      },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
      },
    };
  }

  private getElement(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing element: ${selector}`);
    return element;
  }
}
